/** UDYOG (উদ্যোগ) youth-entrepreneur application, Bangladesh Bank — data helpers. */

import {
    displayField,
    formatDateField,
    parseDateField,
    toBengaliDigits,
} from '../voter-migration-form/voterMigrationFormEngine';

export { displayField, formatDateField, parseDateField, toBengaliDigits };

/** The form asks for age on its own closing date (১৫ অক্টোবর ২০২৬). */
export const LAST_DATE = new Date(2026, 9, 15);

/** Eligibility on the closing date: an adult, and no more than 28. */
export const MIN_AGE = 18;
export const MAX_AGE = 28;

export const MARITAL_LABELS = {
    single: 'অবিবাহিত',
    married: 'বিবাহিত',
    widowed: 'বিধবা/বিপত্নীক',
    divorced: 'তালাকপ্রাপ্ত',
};

/** পার্ট-২ is for a planned business, পার্ট-৩ for a running one — one applies. */
export const STAGE_KEYS = ['new', 'running'];

export const ADDRESS_PARTS = ['House', 'Road', 'PostOffice', 'Thana', 'District', 'Division'];

export const COST_COUNT = 12;

const MONEY_ROWS = ['sales', 'profit', 'bizSales', 'bizProfit'];
export const MONEY_PERIODS = ['Monthly', 'Quarterly', 'Yearly'];

export const PART2_KEYS = [
    'businessIdea', 'buyers', 'startTimeline', 'businessType', 'businessLocation', 'productType',
    'rawMaterial', 'productMarket',
    ...['sales', 'profit'].flatMap((row) => MONEY_PERIODS.map((p) => `${row}${p}`)),
    'risks', 'jobsTotal', 'jobsFemale', 'jobsNote', 'marketPosition',
];

export const PART3_KEYS = [
    'bizName', 'bizType', 'bizStartDate', 'tradeLicense', 'avgPrice',
    ...['bizSales', 'bizProfit'].flatMap((row) => MONEY_PERIODS.map((p) => `${row}${p}`)),
    'monthlyCost', 'involvement', 'totalInvestment', 'bizRawMaterial', 'marketingArea',
    'bizMarketPosition', 'bizRisks', 'staffTotal', 'staffFemale', 'staffNote', 'expansionPlan',
];

const blank = (keys) => Object.fromEntries(keys.map((key) => [key, '']));

export const INITIAL_UDYOG_FORM = {
    // পার্ট-১
    nameBn: '',
    nameEn: '',
    fatherName: '',
    motherName: '',
    nid: '',
    birthDate: '',
    maritalStatus: '',
    spouseName: '',
    spouseNid: '',
    education: '',
    phone: '',
    phoneAlt: '',
    email: '',
    training: '',
    experience: '',
    bankAccount: '',
    existingLoan: '',
    ...blank(ADDRESS_PARTS.map((part) => `present${part}`)),
    ...blank(ADDRESS_PARTS.map((part) => `permanent${part}`)),
    sameAddress: '',
    // পার্ট-২ / পার্ট-৩
    businessStage: 'new',
    ...blank(PART2_KEYS),
    ...blank(PART3_KEYS),
    // পার্ট-৪
    capitalRequired: '',
    ownInvestment: '',
    financingRequested: '',
    ...blank(Array.from({ length: COST_COUNT }, (_, i) => i + 1).flatMap((n) => (
        [`cost${n}Detail`, `cost${n}Amount`, `cost${n}Source`]
    ))),
    // পার্ট-৫
    disability: '',
    ethnicMinority: '',
    otherInfo: '',
    applicationDate: '',
};

/** Keys holding taka, typed in digits and printed in Bengali ones. */
export const MONEY_KEYS = new Set([
    'capitalRequired', 'ownInvestment', 'financingRequested', 'totalInvestment',
    ...MONEY_ROWS.flatMap((row) => MONEY_PERIODS.map((p) => `${row}${p}`)),
    ...Array.from({ length: COST_COUNT }, (_, i) => `cost${i + 1}Amount`),
]);

const REQUIRED_KEYS = [
    'nameBn', 'nameEn', 'fatherName', 'motherName', 'nid', 'birthDate', 'maritalStatus',
    'education', 'phone',
    ...ADDRESS_PARTS.map((part) => `present${part}`),
    'capitalRequired', 'financingRequested',
    'applicationDate',
];

const REQUIRED_BY_STAGE = {
    new: ['businessIdea', 'businessType', 'businessLocation'],
    running: ['bizName', 'bizType', 'bizStartDate'],
};

const FORM_SNAPSHOT_KEY = 'udyog-form-last-snapshot';

/** @returns {typeof INITIAL_UDYOG_FORM | null} */
export function getUdyogFormSnapshot() {
    if (typeof window === 'undefined') return null;
    try {
        const raw = localStorage.getItem(FORM_SNAPSHOT_KEY);
        if (!raw) return null;
        return { ...INITIAL_UDYOG_FORM, ...JSON.parse(raw) };
    } catch {
        return null;
    }
}

export function saveUdyogFormSnapshot(form) {
    if (typeof window === 'undefined' || !form) return;
    try {
        const payload = {};
        for (const key of Object.keys(INITIAL_UDYOG_FORM)) {
            payload[key] = displayField(form[key]);
        }
        localStorage.setItem(FORM_SNAPSHOT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

/**
 * Every required blank still empty, named the way the form asks for it — what
 * the download confirmation lists back. Most of this form is "if any", so only
 * the core of each part counts.
 *
 * @param {typeof INITIAL_UDYOG_FORM} form
 * @param {(key: string, opts?: object) => string} t
 */
export function getEmptyUdyogFields(form, t) {
    const keys = [...REQUIRED_KEYS, ...(REQUIRED_BY_STAGE[form?.businessStage] ?? [])];
    if (form?.maritalStatus === 'married') keys.push('spouseName');
    return keys
        .filter((key) => !displayField(form?.[key]))
        .map((key) => t(`udyogForm.fields.${key}`));
}

export function isUdyogFormComplete(form, t) {
    return getEmptyUdyogFields(form, t).length === 0;
}

/** Keep digits and thousands commas, in Bengali numerals. */
export function normalizeMoney(value) {
    return toBengaliDigits(String(value ?? '').replace(/[^\d০-৯,.]/g, ''));
}

const toAscii = (value) => String(value ?? '')
    .replace(/[০-৯]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x09e6 + 48))
    .replace(/,/g, '');

/**
 * The খ table's সর্বমোট: the sum of the amounts, or blank until there is one.
 * @param {Record<string, string>} form
 */
export function costTotal(form) {
    let total = 0;
    for (let i = 1; i <= COST_COUNT; i += 1) {
        const amount = Number(toAscii(form?.[`cost${i}Amount`]));
        if (Number.isFinite(amount)) total += amount;
    }
    if (!total) return '';
    // Lakh-style grouping, the way the amounts above it are written.
    return toBengaliDigits(total.toLocaleString('en-IN', { maximumFractionDigits: 2 }));
}

const daysInPreviousMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 0).getDate();

/**
 * Whole years, then months and days left over, on `on`.
 * @returns {{ years: number, months: number, days: number } | null}
 */
export function ageOn(birth, on = LAST_DATE) {
    if (!(birth instanceof Date) || Number.isNaN(birth.getTime()) || birth > on) return null;
    let years = on.getFullYear() - birth.getFullYear();
    let months = on.getMonth() - birth.getMonth();
    let days = on.getDate() - birth.getDate();
    if (days < 0) {
        months -= 1;
        days += daysInPreviousMonth(on);
    }
    if (months < 0) {
        years -= 1;
        months += 12;
    }
    return { years, months, days };
}

/**
 * What is wrong with a birth date for this programme, or null when nothing is.
 * Age counts on the closing date, as the form and the programme's rules do.
 * @param {Date | null} birth
 * @returns {{ kind: 'notBorn' } | { kind: 'under' | 'over', age: number } | null}
 */
export function birthDateIssue(birth, on = LAST_DATE) {
    if (!(birth instanceof Date) || Number.isNaN(birth.getTime())) return null;
    const age = ageOn(birth, on);
    if (!age) return { kind: 'notBorn' };
    if (age.years < MIN_AGE) return { kind: 'under', age: age.years };
    if (age.years > MAX_AGE) return { kind: 'over', age: age.years };
    return null;
}

/** Everything the preview prints, already trimmed, plus the composed lines. */
export function buildUdyogForm(form) {
    const out = {};
    for (const key of Object.keys(INITIAL_UDYOG_FORM)) {
        out[key] = displayField(form?.[key]);
    }

    out.nameEnUpper = out.nameEn.toUpperCase();
    out.maritalLabel = MARITAL_LABELS[out.maritalStatus] ?? '';
    out.phoneLine = [out.phone, out.phoneAlt].filter(Boolean).join(' / ');

    const age = ageOn(parseDateField(out.birthDate));
    out.birthLine = out.birthDate && age
        ? `${out.birthDate}  (বয়স: ${toBengaliDigits(age.years)} বছর ${toBengaliDigits(age.months)} মাস ${toBengaliDigits(age.days)} দিন)`
        : out.birthDate;

    if (out.sameAddress) {
        for (const part of ADDRESS_PARTS) out[`permanent${part}`] = out[`present${part}`];
    }

    // Spouse details print only for someone who has a spouse to name.
    if (out.maritalStatus !== 'married') {
        out.spouseName = '';
        out.spouseNid = '';
    }

    // Only the part that applies prints; the other stays blank for the bank.
    for (const key of out.businessStage === 'running' ? PART2_KEYS : PART3_KEYS) out[key] = '';

    out.costTotal = costTotal(form);
    return out;
}
